import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Button, useChatStream } from '@airia/ui';
import { getRequiredConfig } from '../../lib/service-discovery';
import { getEmailContext } from '../../lib/outlook';
import { useUserId } from '../../lib/useUserId';
import { useBrowserAgentVariables } from '../McpSessionProvider';
import { LoadingSpinner } from '../../ui/loading-spinner';
import { ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import { ProfileDropdown } from '../playbook/ProfileDropdown';
import { SignalRStatusDot } from '../SignalRStatusDot';
const CustomReadView = ({ agentId, onSwitchToReplyMode }) => {
    const { user } = useAuth();
    const { apiUrl } = getRequiredConfig();
    const { userId } = useUserId();
    const browserAgentVariables = useBrowserAgentVariables();
    const [htmlContent, setHtmlContent] = useState(null);
    const [error, setError] = useState(null);
    const hasExecutedRef = useRef(false);
    const chatStream = useChatStream({
        apiUrl,
        agentId,
        bearerToken: user?.access_token ?? '',
    });
    // Map StreamMessage[] to the format expected by the component
    const allMessages = useMemo(() => {
        return chatStream.messages
            .filter((m) => m.message != null && m.message !== '')
            .map((m) => ({
            id: m.id,
            value: m.message,
            role: m.role,
        }));
    }, [chatStream.messages]);
    // Auto-execute pipeline when component mounts
    useEffect(() => {
        if (!userId || !user?.access_token || hasExecutedRef.current)
            return;
        hasExecutedRef.current = true;
        executePipeline();
    }, [userId, user?.access_token]);
    const executePipeline = useCallback(async () => {
        if (!userId)
            return;
        setHtmlContent(null);
        setError(null);
        try {
            const emailContext = await getEmailContext();
            await chatStream.submit({
                data: {
                    userInput: emailContext,
                    userId,
                    variables: browserAgentVariables,
                },
            });
        }
        catch (err) {
            console.error('Error executing custom read view pipeline:', err);
            setError('Failed to execute custom analysis. Please try again.');
        }
    }, [userId, chatStream.submit, browserAgentVariables]);
    // Extract HTML content from pipeline response
    useEffect(() => {
        if (allMessages.length > 0 && !chatStream.isLoading) {
            const lastMessage = allMessages[allMessages.length - 1];
            if (lastMessage?.role === 'assistant') {
                setHtmlContent(lastMessage.value);
            }
        }
    }, [allMessages, chatStream.isLoading]);
    const handleRefresh = () => {
        hasExecutedRef.current = false;
        executePipeline();
        hasExecutedRef.current = true;
    };
    return (_jsxs("div", { className: "flex flex-col h-full", children: [_jsx("div", { className: "flex-shrink-0 p-4 border-b bg-background", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "flex items-center gap-2", children: _jsx("div", { className: "text-sm font-medium text-foreground", children: "Custom Analysis" }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Button, { variant: "ghost", size: "icon", onClick: handleRefresh, disabled: chatStream.isLoading, title: "Refresh analysis", className: "h-8 w-8", children: _jsx(RefreshCw, { className: `size-4 ${chatStream.isLoading ? 'animate-spin' : ''}` }) }), _jsxs(Button, { variant: "outline", size: "sm", onClick: onSwitchToReplyMode, className: "text-xs", children: [_jsx(ArrowLeft, { className: "size-3 mr-1" }), "Reply Mode"] }), _jsx(SignalRStatusDot, {}), _jsx(ProfileDropdown, {})] })] }) }), _jsxs("div", { className: "flex-1 overflow-hidden", children: [chatStream.isLoading && (_jsxs("div", { className: "flex flex-col items-center justify-center py-8", children: [_jsx(LoadingSpinner, {}), _jsx("span", { className: "text-sm text-muted-foreground mt-2", children: "Analyzing email..." })] })), error && !chatStream.isLoading && (_jsxs("div", { className: "flex flex-col items-center justify-center py-8", children: [_jsx(AlertCircle, { className: "size-8 text-destructive mb-2" }), _jsx("span", { className: "text-sm text-destructive", children: error }), _jsx(Button, { variant: "outline", size: "sm", onClick: handleRefresh, className: "mt-4", children: "Try Again" })] })), htmlContent && !chatStream.isLoading && (_jsx("iframe", { srcDoc: htmlContent, title: "Custom Analysis", className: "w-full h-full border-0", sandbox: "allow-same-origin allow-scripts allow-forms allow-modals allow-popups" }))] })] }));
};
export default CustomReadView;
